import {NgModule} from '@angular/core';
import {RouterModule, Route, Routes, ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';

import { TermsOfServiceComponent } from './web/terms-of-service/terms-of-service.component';
import { PrivacyPolicyComponent } from './web/privacy-policy/privacy-policy.component';
import { WeblibraryComponent } from './web/weblibrary/weblibrary.component';
import { WeblibraryNewAiSearchComponent } from './web/weblibrary-new-ai-search/weblibrary-new-ai-search.component';
import { useNewLibrary, discoverLanding, renderLanding, renderOnly } from './shared/feature-flags';
import { WebEditCollectionComponent } from './web/webedit-collection/webedit-collection.component';
import { WebPortfolioComponent } from './web/webportfolio/webportfolio.component';
import { WebuploadsandboxComponent } from './web/webuploadsandbox/webuploadsandbox.component';
import { ServiceSettingsComponent } from './pages/service-settings/service-settings.component';
import { WebHomeComponent } from './web/webhome/webhome.component';
import { Platform } from '@angular/cdk/platform';
import { WebMobileRestrictionGuard } from './shared/providers/mobile-guard';
import { WebSharedUserContentComponent } from './web/web-shared-user-content/web-shared-user-content.component';
import { WebStoreComponent } from './web/web-store/web-store.component';
import { WebReviewComponent } from './web/web-review/web-review.component';
import { Landing2025Component } from './shared/components/landing2025/landing2025.component';
import { ClassicLandingComponent } from './shared/components/classic-landing/classic-landing.component';
import { ExtendedStoreComponent } from './web/extended-store/extended-store.component';
import { AdminGuard } from './shared/providers/admin.guard';
import { DiscoverLandingComponent } from './web/discover-landing/discover-landing.component';
import { RENDER_ROUTES } from './web/render/render.routes';
import { RenderLegalComponent } from './web/render/legal/render-legal.component';
import { environment } from 'src/environments/environment';


const SERVICE_ROUTE: Route = { path: 'service', component: ServiceSettingsComponent, canActivate: [WebMobileRestrictionGuard] };
const ADMIN_ROUTE: Route = {
  path: 'admin',
  loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
  canActivate: [AdminGuard],
};
const LEGAL_ROUTES: Routes = [
  { path: 'legal/privacy-policy', component: PrivacyPolicyComponent },  // No guard here
  { path: 'legal/privacy', component: PrivacyPolicyComponent },  // No guard here
  { path: 'legal/terms-of-service', component: TermsOfServiceComponent },  // No guard here
  { path: 'legal/terms', component: TermsOfServiceComponent },  // No guard here
  // The render service's own documents (the boss's set, 2026-11-02; render-legal.data.ts). The ones
  // above stay for memberships and the iOS app.
  { path: 'legal/rendering-terms', component: RenderLegalComponent, data: { doc: 'terms' } },
  { path: 'legal/rendering-privacy', component: RenderLegalComponent, data: { doc: 'privacy' } },
  { path: 'legal/refund-delivery-policy', component: RenderLegalComponent, data: { doc: 'refunds' } },
  { path: 'legal/cookies', component: RenderLegalComponent, data: { doc: 'cookies' } },
];

const routes: Routes = [
  { path: 'landing2025', component: Landing2025Component },
  { path: 'classic-landing', component: ClassicLandingComponent },
  { path: 'new-landing', redirectTo: 'landing2025', pathMatch: 'full' },
  { path: 'get-started', component: Landing2025Component},
  // /discover is the reference-discovery landing ported from the boss's prototype. Flag-gated like
  // extended-store: the route does not exist outside the dev environments, no UI links to it.
  // See docs/landing-discover-port.md.
  ...(discoverLanding
    ? [{ path: 'discover', component: DiscoverLandingComponent, data: { animation: 'homePageAnimation' } }]
    : []),
  // /render is the render product's landing + flow, same gating. See docs/render-landing-port.md.
  ...(renderLanding ? RENDER_ROUTES : []),
  { path: '', component: WebStoreComponent, data: { animation: 'homePageAnimation' } },
  // extended-store is another AI-search surface (unlinked, URL-only). Gate it with the same
  // flag so it is not reachable on production while the new library is off.
  ...(useNewLibrary
    ? [{ path: 'extended-store', component: ExtendedStoreComponent, data: { animation: 'homePageAnimation' } }]
    : []),
  { path: 'library/edit', component: WebEditCollectionComponent, data: { animation: 'editCollectionPageAnimation' }, canActivate: [WebMobileRestrictionGuard] },
  // /library resolves to the AI-search library only where environment.useNewLibrary is on
  // (dev builds). Production keeps the classic library — byte-identical to 2b0caa02. Flip the
  // flag in environment.web.prod.ts + redeploy to ship the new one. See also web-root.component.html.
  { path: 'library', component: useNewLibrary ? WeblibraryNewAiSearchComponent : WeblibraryComponent, data: { animation: 'libraryPageAnimation' } },
  // Direct route for side-by-side comparison. Gated by the same flag: without it the new
  // library must not be reachable on production even by typing the URL.
  ...(useNewLibrary
    ? [{ path: 'library-new', component: WeblibraryNewAiSearchComponent, data: { animation: 'libraryPageAnimation' } }]
    : []),
  { path: 'uploadsandbox', component: WebuploadsandboxComponent, canActivate: [WebMobileRestrictionGuard] },
  { path: 'home', component: WebHomeComponent, data: { animation: 'homePageAnimation' }, canActivate: [WebMobileRestrictionGuard] },
  { path: 'review', component: WebReviewComponent, data: { reviewStatus: 'in-review' }, canActivate: [WebMobileRestrictionGuard] },
  { path: 'in-review', component: WebReviewComponent, data: { reviewStatus: 'in-review' }, canActivate: [WebMobileRestrictionGuard] },

  // updated route for shared model handling:
  { path: 'model/:modelId', component: WebSharedUserContentComponent, canActivate: [WebMobileRestrictionGuard] },
  { path: 'collection/:collectionId', component: WebSharedUserContentComponent, canActivate: [WebMobileRestrictionGuard] },

  { path: 'portfolio', component: WebPortfolioComponent, data: { animation: 'homePageAnimation' }, canActivate: [WebMobileRestrictionGuard] },
  { path: 'pending-review', component: WebPortfolioComponent, data: { animation: 'homePageAnimation', reviewMode: true }, canActivate: [WebMobileRestrictionGuard] },

  SERVICE_ROUTE,
  ADMIN_ROUTE,
  ...LEGAL_ROUTES,
  { path: '**', redirectTo: '' }
];

// A render-only site (`renderOnly`, feature-flags.ts): the render landing at the root, its flow, the
// legal pages and /admin; /service only where debug pages are shown. Any other address — the old
// site's included — leads home.
const RENDER_ONLY_ROUTES: Routes = [
  ...RENDER_ROUTES,
  ...LEGAL_ROUTES,
  ADMIN_ROUTE,
  ...(environment.showServicePageAndShowDebugData ? [SERVICE_ROUTE] : []),
  { path: '**', redirectTo: '' },
];

// const routes: Routes = [
  // {path: '', component: WebLandingComponent},
  // {path: 'ourway', canActivate: [RedirectGuard], component: RedirectGuard, data: { externalUrl: 'https://about.3dway.io/'} },
  // {path: 'ourway', component: AvailableSoonComponent},

  // {path: 'solutions', component: AvailableSoonComponent},
  // {path: 'pricing', component: AvailableSoonComponent},
  // {path: 'sales', component: ContactSalesComponent},
  // {path: 'support', component: ContactSupportComponent},
  // {path: 'legal', component: AvailableSoonComponent},
  // {path: 'status3dwayio', canActivate: [RedirectGuard], component: RedirectGuard, data: { externalUrl: 'https://status.3dway.io'} },
  // {path: 'about3dwayio', canActivate: [RedirectGuard], component: RedirectGuard, data: { externalUrl: 'https://about.3dway.io/'} },
  // {path: 'careers3dwayio', canActivate: [RedirectGuard], component: RedirectGuard, data: { externalUrl: 'https://careers.3dway.io'} },
  // {path: '404', component: Error404Component},
// ];

@NgModule({
  imports: [
    RouterModule.forRoot(renderOnly ? RENDER_ONLY_ROUTES : routes, { scrollPositionRestoration: 'top', onSameUrlNavigation: 'reload' }),
  ],
  exports: [RouterModule],
  providers: [
    {
        provide: 'externalUrlRedirectResolver',
        useValue: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
            window.location.href = (route.data as any).externalUrl;
        }
    }
]
})
export class WebRoutingModule {
  constructor(private platform: Platform) {
  }

}
